import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, getToken, HttpError } from "../api";
import { useAuth } from "../auth";
import { ScannerConnection, type ScannerStatus } from "../scanner";
import { SHIPMENT_STATUS_LABELS } from "../types";
import type {
  Code,
  Marketplace,
  OzonCabinet,
  OzonMatchResponse,
  Shipment,
  ShipmentBox,
} from "../types";
import { formatMSK } from "../utils/dates";

const MARKETPLACE_LABELS: Record<Marketplace, string> = {
  wb: "Wildberries",
  ozon: "Ozon",
  yandex: "Яндекс.Маркет",
  other: "другой",
};

function toLocalInput(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function parseFilenameFromContentDisposition(header: string | null): string | null {
  if (!header) return null;
  // Сперва RFC 5987: filename*=UTF-8''<percent-encoded>
  const starMatch = header.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
  if (starMatch) {
    try {
      return decodeURIComponent(starMatch[1].trim());
    } catch {
      /* noop */
    }
  }
  // Фолбэк: обычный filename="..."
  const plain = header.match(/filename\s*=\s*"([^"]+)"/i);
  if (plain) return plain[1];
  return null;
}

function OzonSupplyPanel({ shipment }: { shipment: Shipment }) {
  const { user } = useAuth();
  const canUpload = user?.role === "admin" || user?.role === "marketplace_manager";

  const [match, setMatch] = useState<OzonMatchResponse | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Панель показываем после «Завершить сборку» и пока отгрузка не cancelled;
  // если менеджер явно выбрал WB/Яндекс — скрываем.
  const allowedStatuses: Shipment["status"][] = ["picked", "booked", "exported", "closed"];
  const visible =
    allowedStatuses.includes(shipment.status) &&
    (shipment.marketplace === null || shipment.marketplace === "ozon");

  const refresh = useCallback(async () => {
    if (!visible) return;
    setErr(null);
    try {
      const r = await api.ozonMatch(shipment.id);
      setMatch(r);
    } catch (e) {
      setMatch(null);
      setErr(e instanceof HttpError ? e.message : "сбой");
    }
  }, [shipment.id, visible]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  if (!visible) return null;

  async function onDownload() {
    setBusy(true);
    setErr(null);
    try {
      const res = await fetch(api.ozonSupplyXlsxUrl(shipment.id), {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) {
        let body: { detail?: string; missing_gtins?: string[] } | null = null;
        try {
          body = await res.json();
        } catch {
          /* ignore */
        }
        throw new HttpError(res.status, body?.detail || `HTTP ${res.status}`, body);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      // Имя файла формирует backend (<артикул1>,<артикул2>.xlsx) и кладёт в
      // Content-Disposition. Берём его оттуда — иначе перезатрём красивое имя.
      a.download = parseFilenameFromContentDisposition(
        res.headers.get("content-disposition"),
      ) || `supply-${shipment.id}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setErr(e instanceof HttpError ? `${e.status}: ${e.message}` : "сбой");
    } finally {
      setBusy(false);
    }
  }

  const missing = match?.missing_gtins ?? [];
  const matched = match?.matched ?? [];
  const hasMissing = missing.length > 0;

  return (
    <div className="card">
      <h2>
        Файл поставки для Ozon{" "}
        {matched.length > 0 && <span className="muted">({matched.length} артикулов)</span>}
      </h2>

      {match === null && !err && <div>Сличаем с Ozon-справочником…</div>}
      {err && <div className="error">{err}</div>}

      {hasMissing && (
        <div className="help-warn" style={{ marginBottom: 12 }}>
          <b>Нет в Ozon-справочнике</b> — {missing.length} GTIN:
          <div className="mono" style={{ fontSize: 12, marginTop: 4 }}>
            {missing.join(", ")}
          </div>
          <div style={{ marginTop: 8 }}>
            {canUpload ? (
              <Link to="/ozon">Залить обновлённый ozn_tov.csv →</Link>
            ) : (
              <span className="muted">
                попросите менеджера маркетплейса или админа залить обновлённый
                <span className="mono"> ozn_tov.csv</span>.
              </span>
            )}
          </div>
        </div>
      )}

      {matched.length > 0 && (
        <table>
          <thead>
            <tr>
              <th>артикул</th>
              <th>имя</th>
              <th className="right">кол-во</th>
            </tr>
          </thead>
          <tbody>
            {matched.map((row) => (
              <tr key={row.article}>
                <td className="mono">{row.article}</td>
                <td>{row.name || "—"}</td>
                <td className="right">{row.qty}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {canUpload && (
        <div className="row" style={{ marginTop: 12 }}>
          <button
            className="primary"
            onClick={onDownload}
            disabled={busy || hasMissing || matched.length === 0}
            title={
              hasMissing
                ? "Сначала доложите недостающие товары в Ozon-справочник"
                : "Скачать readyfile.xlsx для загрузки в ЛК Ozon"
            }
          >
            Скачать readyfile.xlsx
          </button>
          {!hasMissing && (
            <span className="muted" style={{ fontSize: 12 }}>
              формат: артикул / имя / количество — подходит для ЛК Ozon «как есть»
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function DeliveryPanel({
  shipment,
  onSaved,
}: {
  shipment: Shipment;
  onSaved: () => Promise<void> | void;
}) {
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "marketplace_manager";

  const [marketplace, setMarketplace] = useState<Marketplace>(shipment.marketplace ?? "wb");
  const [deliveryNumber, setDeliveryNumber] = useState(shipment.delivery_number ?? "");
  const [slotAt, setSlotAt] = useState(toLocalInput(shipment.slot_at));
  const [markingZip, setMarkingZip] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  useEffect(() => {
    setMarketplace(shipment.marketplace ?? "wb");
    setDeliveryNumber(shipment.delivery_number ?? "");
    setSlotAt(toLocalInput(shipment.slot_at));
  }, [shipment.marketplace, shipment.delivery_number, shipment.slot_at]);

  // Панель видна только когда отгрузка уже собрана (picked) или уже забронирована.
  // До picked (scanning) менеджеру делать нечего; после exported/closed номер
  // уже не правится — показываем read-only блок для справки.
  const visibleStatuses: Shipment["status"][] = [
    "picked",
    "booked",
    "exported",
    "closed",
  ];
  if (!visibleStatuses.includes(shipment.status)) return null;

  const canEditStatus = shipment.status === "picked" || shipment.status === "booked";
  const readOnly = !canEdit || !canEditStatus;
  const header =
    shipment.status === "picked"
      ? "Бронирование поставки"
      : "Поставка на маркетплейс";

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    setOk(null);
    if (!deliveryNumber.trim()) {
      setErr("номер поставки обязателен");
      return;
    }
    if (!slotAt) {
      setErr("укажите слот");
      return;
    }
    if (!markingZip) {
      setErr("приложите ZIP со стикерами оклейки коробов (из ЛК маркетплейса)");
      return;
    }
    if (!markingZip.name.toLowerCase().endsWith(".zip")) {
      setErr("нужен файл .zip — другие форматы не принимаются");
      return;
    }
    setBusy(true);
    try {
      await api.submitDelivery(
        shipment.id,
        {
          marketplace,
          delivery_number: deliveryNumber.trim(),
          slot_at: new Date(slotAt).toISOString(),
        },
        markingZip,
      );
      setOk("сохранено, уведомление отправлено в Bitrix");
      setMarkingZip(null);
      await onSaved();
    } catch (e2) {
      setErr(e2 instanceof HttpError ? `${e2.status}: ${e2.message}` : "сбой");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card">
      <h2>{header}</h2>

      {readOnly ? (
        <div className="row">
          <div>
            <div className="muted">маркетплейс</div>
            <div>
              {shipment.marketplace ? MARKETPLACE_LABELS[shipment.marketplace] : "—"}
            </div>
          </div>
          <div>
            <div className="muted">номер поставки</div>
            <div className="mono">{shipment.delivery_number || "—"}</div>
          </div>
          <div>
            <div className="muted">слот</div>
            <div>{formatMSK(shipment.slot_at)}</div>
          </div>
          <div>
            <div className="muted">забронировано</div>
            <div>
              {formatMSK(shipment.booked_at)}
            </div>
          </div>
        </div>
      ) : (
        <form onSubmit={onSubmit}>
          <div className="row">
            <label>
              <div className="muted">маркетплейс</div>
              <select
                value={marketplace}
                onChange={(e) => setMarketplace(e.target.value as Marketplace)}
                disabled={busy}
              >
                {(Object.keys(MARKETPLACE_LABELS) as Marketplace[]).map((m) => (
                  <option key={m} value={m}>
                    {MARKETPLACE_LABELS[m]}
                  </option>
                ))}
              </select>
            </label>
            <label className="grow">
              <div className="muted">номер поставки</div>
              <input
                type="text"
                value={deliveryNumber}
                onChange={(e) => setDeliveryNumber(e.target.value)}
                placeholder="напр. WB-778899"
                className="mono"
                maxLength={64}
                disabled={busy}
              />
            </label>
            <label>
              <div className="muted">слот (дата и время)</div>
              <input
                type="datetime-local"
                value={slotAt}
                onChange={(e) => setSlotAt(e.target.value)}
                disabled={busy}
              />
            </label>
            <button className="primary" disabled={busy || !markingZip}>
              {shipment.status === "booked" ? "Обновить" : "Сохранить и отправить"}
            </button>
          </div>
          <div className="row" style={{ marginTop: 8, alignItems: "center", gap: 8 }}>
            <label className="grow">
              <div className="muted">маркировка коробов (ZIP из ЛК маркетплейса) *</div>
              <input
                type="file"
                accept=".zip,application/zip,application/x-zip-compressed"
                onChange={(e) => setMarkingZip(e.target.files?.[0] ?? null)}
                disabled={busy}
              />
            </label>
            {shipment.marking_zip_filename && (
              <a
                href={api.markingZipUrl(shipment.id)}
                className="muted"
                style={{ fontSize: 12 }}
                title={`загружено ${formatMSK(shipment.marking_zip_uploaded_at)}`}
              >
                текущий: {shipment.marking_zip_filename}
              </a>
            )}
          </div>
          {err && <div className="error">{err}</div>}
          {ok && <div className="ok">{ok}</div>}
        </form>
      )}
      {readOnly && shipment.marking_zip_filename && (
        <div style={{ marginTop: 8 }}>
          <div className="muted">маркировка коробов</div>
          <a href={api.markingZipUrl(shipment.id)}>
            📦 Скачать {shipment.marking_zip_filename}
          </a>
          <span className="muted" style={{ marginLeft: 8, fontSize: 12 }}>
            загружено {formatMSK(shipment.marking_zip_uploaded_at)}
          </span>
        </div>
      )}
    </div>
  );
}

function ScannerIndicator({ status }: { status: ScannerStatus }) {
  const labels: Record<ScannerStatus["kind"], string> = {
    unsupported: "недоступен в этом браузере",
    disconnected: "сканер не подключён",
    connecting: "подключение…",
    connected: "сканер подключён",
    error: "ошибка сканера",
  };
  const title = status.kind === "error" ? `${labels.error}: ${status.message}` : labels[status.kind];
  return (
    <span className={`scanner-indicator scanner-${status.kind}`} title={title}>
      <span className="scanner-dot" />
      {labels[status.kind]}
    </span>
  );
}

function BoxesPanel({
  planned,
  printed,
  busyNumber,
  onPrint,
  onAddBox,
  addBusy,
  active,
}: {
  planned: number;
  printed: ShipmentBox[];
  busyNumber: number | null;
  onPrint: (boxNumber: number, alreadyPrinted: boolean) => void;
  onAddBox: () => void;
  addBusy: boolean;
  active: number | null;
}) {
  const printedSet = new Map(printed.map((b) => [b.box_number, b]));
  const items = Array.from({ length: planned }, (_, i) => i + 1);
  const printedCount = items.filter((n) => printedSet.get(n)?.printed_at).length;
  return (
    <div
      style={{
        flex: "0 0 110px",
        borderLeft: "1px solid var(--border, #e0e0e0)",
        paddingLeft: 12,
      }}
    >
      <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>
        Стикеры коробов 58×40
      </div>
      <div className="muted" style={{ fontSize: 11, marginBottom: 8 }}>
        отпечатано {printedCount} из {planned}
      </div>
      <div
        title="В этот короб записываются новые сканы — из этого собирается упаковочный лист для Ozon. Сменить: нажмите на другой распечатанный короб."
        style={{
          fontSize: 13,
          fontWeight: 700,
          marginBottom: 8,
          padding: "4px 6px",
          borderRadius: 4,
          background: active ? "#e3f6e8" : "#fff4e0",
          color: active ? "#136c2e" : "#8a5a00",
        }}
      >
        {active ? `Сканы → короб ${active}` : "Короб не выбран — напечатайте стикер"}
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 6,
          maxHeight: 460,
          overflowY: "auto",
        }}
      >
        {items.map((n) => {
          const box = printedSet.get(n);
          const isPrinted = !!box?.printed_at;
          const isActive = active === n;
          const busy = busyNumber === n;
          return (
            <button
              key={n}
              type="button"
              onClick={() => onPrint(n, isPrinted)}
              disabled={busy}
              title={
                isActive
                  ? `короб ${n} — сейчас сканы идут в него (клик — перепечатать стикер)`
                  : isPrinted
                    ? `короб ${n} — распечатан${box && box.print_count > 1 ? ` ×${box.print_count}` : ""} (клик — сделать текущим)`
                    : `печать стикера короба ${n} (станет текущим)`
              }
              style={{
                width: 44,
                height: 44,
                fontSize: 18,
                fontWeight: 700,
                border: isActive ? "3px solid #18a34a" : "1px solid #888",
                borderRadius: 4,
                cursor: busy ? "wait" : "pointer",
                background: isActive ? "#18a34a" : isPrinted ? "#d24" : "#f3f3f3",
                color: isPrinted ? "#fff" : "#222",
                opacity: busy ? 0.6 : 1,
              }}
            >
              {n}
            </button>
          );
        })}
      </div>
      <button
        type="button"
        onClick={onAddBox}
        disabled={addBusy}
        title="увеличить план коробов на 1 (если по факту больше, чем при создании отгрузки)"
        style={{
          marginTop: 10,
          width: "100%",
          padding: "6px 0",
          fontSize: 12,
          cursor: addBusy ? "wait" : "pointer",
        }}
      >
        {addBusy ? "…" : "+ короб"}
      </button>
    </div>
  );
}

function ModalShell({
  onCancel,
  children,
}: {
  onCancel?: () => void;
  children: React.ReactNode;
}) {
  return (
    <div
      onClick={(e) => {
        if (onCancel && e.target === e.currentTarget) onCancel();
      }}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.45)",
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {children}
    </div>
  );
}

function KitModePromptModal({
  shipmentNumber,
  busy,
  onAnswer,
}: {
  shipmentNumber: string;
  busy: boolean;
  onAnswer: (isKit: boolean) => void;
}) {
  // Фаза 1: Стартовый вопрос перед сканированием. Пока оператор не ответил —
  // is_kit_order=null в БД, и каждый заход на эту страницу показывает модалку.
  // Бэкенд гарантирует, что ответ можно сохранить только пока в отгрузке нет items.
  return (
    <ModalShell>
      <div
        className="card"
        style={{ minWidth: 380, maxWidth: 520, background: "#fff" }}
      >
        <h2 style={{ marginTop: 0 }}>Эта отгрузка содержит наборы?</h2>
        <div className="muted" style={{ marginBottom: 16 }}>
          Отгрузка <span className="mono">{shipmentNumber}</span>. Перед началом
          сканирования укажите, как сгруппирован товар. От этого зависит:
          количество в файле для Ozon и подсчёт прогресса.
        </div>
        <ul style={{ marginBottom: 16, paddingLeft: 20 }}>
          <li>
            <b>Наборы / комплекты</b> — несколько КИЗ упакованы как один товар
            (например, набор из 3 цветов одного артикула).
          </li>
          <li>
            <b>Одиночные товары</b> — каждый КИЗ продаётся отдельно.
          </li>
        </ul>
        <div className="row" style={{ justifyContent: "flex-end", gap: 8 }}>
          <button
            onClick={() => onAnswer(false)}
            disabled={busy}
            title="Каждый КИЗ — отдельный товар"
          >
            Нет, одиночные товары
          </button>
          <button
            className="primary"
            onClick={() => onAnswer(true)}
            disabled={busy}
            title="Несколько КИЗ — это один набор"
          >
            Да, наборы
          </button>
        </div>
      </div>
    </ModalShell>
  );
}

function KitConfirmSizeModal({
  size,
  busy,
  onConfirm,
  onReset,
}: {
  size: number;
  busy: boolean;
  onConfirm: () => void;
  onReset: () => void;
}) {
  // Фаза 2: после клика «Набор собран» — подтверждение размера. Если оператор
  // ошибся (отсканировал лишнее или забыл закрыть набор) — сбрасывает обучение
  // и пересдаёт. После «Да, продолжить» kit_size фиксируется в БД иммутабельно.
  return (
    <ModalShell>
      <div
        className="card"
        style={{ minWidth: 380, maxWidth: 480, background: "#fff" }}
      >
        <h2 style={{ marginTop: 0 }}>В наборе {size} шт — верно?</h2>
        <div className="muted" style={{ marginBottom: 16 }}>
          После подтверждения этот размер зафиксируется на всю отгрузку. Каждые{" "}
          {size} следующих сканов система будет считать как новый набор.
        </div>
        <div className="row" style={{ justifyContent: "flex-end", gap: 8 }}>
          <button
            onClick={onReset}
            disabled={busy}
            title="Удалить отсканированные коды и пересдать обучение"
          >
            Нет, пересдать
          </button>
          <button
            className="primary"
            onClick={onConfirm}
            disabled={busy}
            title={`Зафиксировать kit_size=${size}`}
          >
            Да, продолжить
          </button>
        </div>
      </div>
    </ModalShell>
  );
}

function PickFinalizeModal({
  shipment,
  initialBoxes,
  printedCount,
  busy,
  onCancel,
  onAccept,
}: {
  shipment: Shipment;
  initialBoxes: number;
  printedCount: number;
  busy: boolean;
  onCancel: () => void;
  onAccept: (boxesActual: number) => void;
}) {
  const [value, setValue] = useState<string>(String(initialBoxes));
  const num = Math.max(0, Math.floor(Number(value) || 0));
  const pallets = num > 0 ? Math.ceil(num / 16) : 0;
  return (
    <div
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.45)",
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        className="card"
        style={{ minWidth: 360, maxWidth: 480, background: "#fff" }}
      >
        <h2 style={{ marginTop: 0 }}>Завершить сборку</h2>
        <div className="muted" style={{ marginBottom: 8 }}>
          Отгрузка <span className="mono">{shipment.number}</span>. План:{" "}
          {shipment.boxes_planned} коробов. Распечатано стикеров: {printedCount}.
        </div>
        <div style={{ marginBottom: 12 }}>
          <b>Итог: {num} коробов / {pallets} паллет.</b>
        </div>
        <div style={{ marginBottom: 12 }}>
          <label>
            <div className="muted" style={{ fontSize: 12 }}>
              факт коробов (можно исправить)
            </div>
            <input
              type="number"
              min={0}
              max={999}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              style={{ width: 120 }}
              autoFocus
            />
          </label>
        </div>
        <div className="muted" style={{ fontSize: 12, marginBottom: 12 }}>
          паллеты считаются автоматически (16 коробов = 1 паллет).
          После «Принять» состав отгрузки нельзя будет менять.
        </div>
        <div className="row" style={{ justifyContent: "flex-end", gap: 8 }}>
          <button onClick={onCancel} disabled={busy}>
            Отмена
          </button>
          <button
            className="primary"
            onClick={() => onAccept(num)}
            disabled={busy}
          >
            Принять
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ShipmentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const shipmentId = Number(id);
  const [shipment, setShipment] = useState<Shipment | null>(null);
  const [codes, setCodes] = useState<Code[] | null>(null);
  // Виртуализация списка кодов: на отгрузках 2000+ КИЗов рендер всех строк
  // (~17k DOM-узлов) вешал вкладку на минуты и морозил каждый скан. Рендерим
  // только видимое окно. Данные (codes) и счётчик (codes.length) не меняются —
  // виртуализируется ТОЛЬКО отображение.
  const codesScrollRef = useRef<HTMLDivElement | null>(null);
  const prevCodesLen = useRef(0);
  const rowVirtualizer = useVirtualizer({
    count: codes?.length ?? 0,
    getScrollElement: () => codesScrollRef.current,
    estimateSize: () => 37, // th/td padding 8+8 + строка + border ≈ 37px; уточняется measureElement
    overscan: 12,
  });
  const [err, setErr] = useState<string | null>(null);
  const [scanInput, setScanInput] = useState("");
  const [lastScanOk, setLastScanOk] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [scannerStatus, setScannerStatus] = useState<ScannerStatus>(() =>
    ScannerConnection.isSupported() ? { kind: "disconnected" } : { kind: "unsupported" },
  );
  const scannerRef = useRef<ScannerConnection | null>(null);

  // Стикеры коробов 58×40: справа от рабочей зоны рисуем столбец иконок
  // 1..boxes_planned. Серые = ещё не печатали, красные = printed_at!=null.
  const [boxes, setBoxes] = useState<ShipmentBox[]>([]);
  const [boxBusy, setBoxBusy] = useState<number | null>(null);
  const [addBoxBusy, setAddBoxBusy] = useState(false);

  // На любую новую ошибку показываем красный полноэкранный flash на 3с,
  // чтобы оператор за сканером гарантированно заметил сбой (раньше «сбой»
  // выводился мелким текстом ниже формы и тонул в потоке).
  const [errorFlash, setErrorFlash] = useState(false);
  const lastErrRef = useRef<string | null>(null);
  useEffect(() => {
    if (err && err !== lastErrRef.current) {
      lastErrRef.current = err;
      setErrorFlash(true);
      const t = setTimeout(() => setErrorFlash(false), 3000);
      return () => clearTimeout(t);
    }
    if (!err) lastErrRef.current = null;
  }, [err]);

  // Модалка финализации «Итог: K коробов = M паллет — Принять / Исправить».
  // Открывается на «Завершить сборку», если у отгрузки задан boxes_planned.
  const [pickDialog, setPickDialog] = useState<{ boxesActual: number } | null>(
    null,
  );

  // Kit-mode UI state.
  // - kitBusy: PATCH-запрос в полёте (для disable кнопок в модалках).
  // - confirmKitOpen: открыта ли вторая модалка «В наборе X шт — верно?»
  const [kitBusy, setKitBusy] = useState(false);
  const [confirmKitOpen, setConfirmKitOpen] = useState(false);

  // Реестр кабинетов Ozon (юрлиц) — чтобы показать имя юрлица отгрузки.
  const [cabinets, setCabinets] = useState<OzonCabinet[]>([]);

  const refresh = useCallback(async () => {
    try {
      // Один запрос /api/shipments/{id}/codes возвращает сразу массив Code.
      // Раньше брали list[int] и делали N×getCode — на 3000+ кизах страница
      // вешалась на initial load (тысячи параллельных GET, nginx таймаут).
      const [sh, codeObjs, boxRows] = await Promise.all([
        api.getShipment(shipmentId),
        api.listShipmentCodes(shipmentId),
        api.listShipmentBoxes(shipmentId),
      ]);
      setShipment(sh);
      setCodes(codeObjs);
      setBoxes(boxRows);
    } catch (e) {
      setErr(e instanceof HttpError ? e.message : "сбой");
    }
  }, [shipmentId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    api.listCabinets().then(setCabinets).catch(() => setCabinets([]));
  }, []);

  // Автоскролл к только что отсканированному коду. Скроллим ТОЛЬКО при
  // инкременте (len = prev+1 — свежий скан), а не при первичной загрузке
  // большой отгрузки (null→N), иначе список прыгал бы вниз при открытии.
  useEffect(() => {
    const len = codes?.length ?? 0;
    if (len > 0 && len === prevCodesLen.current + 1) {
      rowVirtualizer.scrollToIndex(len - 1, { align: "end" });
    }
    prevCodesLen.current = len;
  }, [codes?.length, rowVirtualizer]);

  const submitKey = useCallback(
    async (key: string): Promise<void> => {
      const clean = key.trim();
      if (!clean) return;
      setErr(null);
      setLastScanOk(null);
      try {
        // Раньше после каждого скана дёргали refresh() → listShipmentCodeIds +
        // N×getCode. На отгрузках 1000+ кизов это делало ~1М запросов и
        // браузер/nginx падали по таймауту. Теперь делаем один POST add +
        // один GET для свежего кода и пушим его в локальный массив.
        const { code_id } = await api.addCodeToShipment(shipmentId, {
          composite_key: clean,
        });
        const fresh = await api.getCode(code_id);
        setCodes((prev) =>
          prev && prev.some((c) => c.id === fresh.id) ? prev : [...(prev ?? []), fresh],
        );
        // Первый успешный скан переводит draft → scanning на бэке; обновим
        // локальный статус, чтобы UI-кнопки (Завершить сборку и т.п.) ожили
        // без дополнительного запроса.
        setShipment((sh) =>
          sh && sh.status === "draft" ? { ...sh, status: "scanning" } : sh,
        );
        // Kit-режим: счётчик «В наборе» и гейт «Набор собран» питаются от
        // kit_batches.items_count активной партии, а полного refresh после
        // скана больше нет (см. выше) — двигаем счётчик локально; сервер
        // остаётся источником истины при следующей загрузке отгрузки.
        setShipment((sh) => {
          if (!sh?.is_kit_order || !sh.kit_batches?.length) return sh;
          const activeNo = Math.max(...sh.kit_batches.map((b) => b.batch_no));
          return {
            ...sh,
            kit_batches: sh.kit_batches.map((b) =>
              b.batch_no === activeNo
                ? { ...b, items_count: b.items_count + 1 }
                : b,
            ),
          };
        });
        setLastScanOk(`+ ${clean}`);
      } catch (e) {
        setErr(e instanceof HttpError ? `${e.status}: ${e.message}` : "сбой");
      }
    },
    [shipmentId],
  );

  async function onScan(e: FormEvent) {
    e.preventDefault();
    const key = scanInput.trim();
    if (!key) return;
    setBusy(true);
    try {
      await submitKey(key);
      setScanInput("");
      inputRef.current?.focus();
    } finally {
      setBusy(false);
    }
  }

  async function onConnectScanner() {
    if (!scannerRef.current) {
      scannerRef.current = new ScannerConnection({
        onStatusChange: setScannerStatus,
        onScan: (p) => {
          void submitKey(p.compositeKey);
        },
        onError: (msg) => setErr(`сканер: ${msg}`),
      });
    }
    await scannerRef.current.connect();
  }

  async function onDisconnectScanner() {
    await scannerRef.current?.disconnect();
  }

  useEffect(() => {
    return () => {
      void scannerRef.current?.disconnect();
    };
  }, []);

  async function onRemove(codeId: number) {
    if (!confirm("Убрать код из отгрузки?")) return;
    try {
      await api.removeCodeFromShipment(shipmentId, codeId);
      // Локально вычеркиваем — без полного refresh, иначе на больших отгрузках
      // удаление одного кода стоило бы ~1000 запросов на перерисовку списка.
      setCodes((prev) => (prev ? prev.filter((c) => c.id !== codeId) : prev));
      // Kit-режим: items_count партии изменился на сервере, а фронт не знает,
      // из какой партии был код — перечитываем отгрузку одним GET (удаление
      // редкое, это не путь массового скана).
      if (shipment?.is_kit_order) {
        try {
          setShipment(await api.getShipment(shipmentId));
        } catch {
          // не критично: сверится при следующей загрузке страницы
        }
      }
    } catch (e) {
      setErr(e instanceof HttpError ? e.message : "сбой");
    }
  }

  async function onExport() {
    // используем прямое скачивание с токеном в header: fetch + blob + download
    try {
      setBusy(true);
      const res = await fetch(api.exportUrl(shipmentId), {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `shipment-${shipmentId}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      await refresh();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "сбой экспорта");
    } finally {
      setBusy(false);
    }
  }

  async function onPackingList() {
    // Упаковочный лист — только чтение, статус отгрузки не меняет (в отличие от CSV).
    try {
      setBusy(true);
      setErr(null);
      const res = await fetch(api.packingListUrl(shipmentId), {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error(`упаковочный лист: HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download =
        parseFilenameFromContentDisposition(res.headers.get("content-disposition")) ||
        `packing-list-${shipmentId}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "сбой выгрузки упаковочного листа");
    } finally {
      setBusy(false);
    }
  }

  function onPick() {
    // Если у отгрузки заведён план коробов — открываем модалку «Итог», где
    // оператор подтверждает или исправляет boxes_actual. Без плана коробов
    // (старые отгрузки) — обычный confirm, как раньше.
    if (shipment?.boxes_planned && shipment.boxes_planned > 0) {
      const printed = boxes.filter((b) => b.printed_at !== null).length;
      setPickDialog({ boxesActual: printed });
      return;
    }
    if (!confirm("Завершить сборку? После этого состав отгрузки нельзя будет менять.")) return;
    void doPick(null);
  }

  async function doPick(boxesActual: number | null): Promise<void> {
    setBusy(true);
    setErr(null);
    try {
      if (boxesActual !== null) {
        await api.finalizeBoxes(shipmentId, boxesActual);
      }
      await api.pickShipment(shipmentId);
      setPickDialog(null);
      await refresh();
    } catch (e) {
      // Особый кейс: kit-режим, последний набор недозаполнен.
      // Бэкенд бросил IncompleteKit (409) — предлагаем принудительно закрыть.
      if (
        e instanceof HttpError &&
        (e.status === 409) &&
        /набор недозаполнен|IncompleteKit/i.test(e.message)
      ) {
        if (
          confirm(
            `${e.message}\n\nЗакрыть всё равно? Это запишется в журнал как принудительное закрытие неполного набора.`,
          )
        ) {
          try {
            await api.pickShipment(shipmentId, { forcePartialKit: true });
            setPickDialog(null);
            await refresh();
            return;
          } catch (e2) {
            setErr(e2 instanceof HttpError ? e2.message : "сбой");
          }
        } else {
          setErr(e.message);
        }
      } else {
        setErr(e instanceof HttpError ? e.message : "сбой");
      }
    } finally {
      setBusy(false);
    }
  }

  async function onAnswerKitMode(isKit: boolean): Promise<void> {
    setKitBusy(true);
    setErr(null);
    try {
      const updated = await api.setKitMode(shipmentId, isKit);
      setShipment(updated);
    } catch (e) {
      setErr(e instanceof HttpError ? e.message : "сбой");
    } finally {
      setKitBusy(false);
    }
  }

  function onMarkKitDone() {
    if (!codes || codes.length === 0) {
      setErr("сначала отсканируйте хотя бы один код");
      return;
    }
    setConfirmKitOpen(true);
  }

  async function onConfirmKitSize(): Promise<void> {
    if (!codes || codes.length === 0) return;
    setKitBusy(true);
    setErr(null);
    try {
      const updated = await api.lockKitSize(shipmentId, codes.length);
      setShipment(updated);
      setConfirmKitOpen(false);
    } catch (e) {
      setErr(e instanceof HttpError ? e.message : "сбой");
    } finally {
      setKitBusy(false);
    }
  }

  async function onResetKitTraining(): Promise<void> {
    if (
      !confirm(
        "Удалить отсканированные коды активной партии и пересдать обучение?",
      )
    ) {
      return;
    }
    setKitBusy(true);
    setErr(null);
    try {
      const updated = await api.resetKitTraining(shipmentId);
      setShipment(updated);
      setConfirmKitOpen(false);
      // После reset items активной партии = 0, перечитаем коды с сервера —
      // закрытые партии могут остаться. Простой ре-фетч.
      try {
        const fresh = await api.listShipmentCodes(shipmentId);
        setCodes(fresh);
      } catch {
        // ignore — UI всё равно покажет последний known state
      }
    } catch (e) {
      setErr(e instanceof HttpError ? e.message : "сбой");
    } finally {
      setKitBusy(false);
    }
  }

  async function onStartNewKitBatch(): Promise<void> {
    if (
      !confirm(
        "Закончить текущую партию и начать новую с другим размером набора?",
      )
    ) {
      return;
    }
    setKitBusy(true);
    setErr(null);
    try {
      const updated = await api.startNewKitBatch(shipmentId);
      setShipment(updated);
    } catch (e) {
      setErr(e instanceof HttpError ? e.message : "сбой");
    } finally {
      setKitBusy(false);
    }
  }

  async function onAddBoxSlot() {
    setAddBoxBusy(true);
    setErr(null);
    try {
      const sh = await api.addBoxSlot(shipmentId);
      setShipment(sh);
    } catch (e) {
      setErr(e instanceof HttpError ? `${e.status}: ${e.message}` : "сбой");
    } finally {
      setAddBoxBusy(false);
    }
  }

  async function onPrintBox(boxNumber: number, alreadyPrinted: boolean) {
    // Распечатанный, но не текущий короб — клик переключает сканы в него
    // (без перепечатки). Перепечатка — кликом по текущему коробу.
    if (alreadyPrinted && shipment?.active_box_number !== boxNumber) {
      if (!confirm(`Сделать короб ${boxNumber} текущим? Новые сканы пойдут в него.`)) return;
      setBoxBusy(boxNumber);
      setErr(null);
      try {
        setShipment(await api.activateBox(shipmentId, boxNumber));
      } catch (e) {
        setErr(e instanceof HttpError ? `${e.status}: ${e.message}` : "сбой переключения короба");
      } finally {
        setBoxBusy(null);
      }
      return;
    }
    if (
      alreadyPrinted &&
      !confirm(`Короб ${boxNumber} уже был распечатан. Точно распечатать повторно?`)
    ) {
      return;
    }
    setBoxBusy(boxNumber);
    setErr(null);
    try {
      const url = api.printBoxLabelUrl(shipmentId, boxNumber);
      const res = await fetch(url, {
        method: "POST",
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) {
        let detail = `HTTP ${res.status}`;
        try {
          const body = await res.json();
          if (body?.detail) detail = body.detail;
        } catch {
          /* ignore */
        }
        throw new HttpError(res.status, detail, null);
      }
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      // Печатаем через скрытый iframe (без новой вкладки и без переключения
      // фокуса). Если у пользователя Chrome запущен с --kiosk-printing —
      // печать уйдёт сразу, без диалога. Иначе — обычный диалог печати.
      const iframe = document.createElement("iframe");
      iframe.style.position = "fixed";
      iframe.style.right = "0";
      iframe.style.bottom = "0";
      iframe.style.width = "0";
      iframe.style.height = "0";
      iframe.style.border = "0";
      iframe.src = blobUrl;
      iframe.onload = () => {
        try {
          iframe.contentWindow?.focus();
          iframe.contentWindow?.print();
        } catch {
          /* fallback: пользователь увидит iframe и нажмёт Ctrl+P */
        }
      };
      document.body.appendChild(iframe);
      // Через минуту забываем blob URL и сам iframe — печать к этому моменту
      // уже стартовала или провалилась.
      setTimeout(() => {
        try {
          document.body.removeChild(iframe);
        } catch {
          /* уже удалён */
        }
        URL.revokeObjectURL(blobUrl);
      }, 60_000);

      // Первая печать открывает короб — сервер сделал его текущим; отражаем сразу.
      if (!alreadyPrinted) {
        setShipment((prev) => (prev ? { ...prev, active_box_number: boxNumber } : prev));
      }
      // Локально помечаем короб распечатанным, чтобы не ждать refresh.
      setBoxes((prev) => {
        const now = new Date().toISOString();
        const idx = prev.findIndex((b) => b.box_number === boxNumber);
        const upd: ShipmentBox =
          idx >= 0
            ? { ...prev[idx], printed_at: now, print_count: (prev[idx].print_count ?? 0) + 1 }
            : { box_number: boxNumber, printed_at: now, printed_by_user_id: null, print_count: 1 };
        const next = [...prev];
        if (idx >= 0) next[idx] = upd;
        else next.push(upd);
        return next.sort((a, b) => a.box_number - b.box_number);
      });
    } catch (e) {
      setErr(e instanceof HttpError ? `${e.status}: ${e.message}` : "сбой печати стикера");
    } finally {
      setBoxBusy(null);
    }
  }

  async function onMarkInTurnover() {
    if (
      !confirm(
        "Подтверждаете, что TechShare принял оборот по этой отгрузке? " +
          "После этого отгрузка будет «Введена в оборот» и её можно передавать в Ozon/WB/B24.",
      )
    )
      return;
    setBusy(true);
    setErr(null);
    try {
      await api.markInTurnover(shipmentId);
      await refresh();
    } catch (e) {
      setErr(e instanceof HttpError ? e.message : "сбой");
    } finally {
      setBusy(false);
    }
  }

  async function onDelete() {
    if (
      !confirm(
        `Удалить отгрузку «${shipment?.number}»?\n\n` +
          "Все коды, привязанные к ней, вернутся в статус «Свободно» и " +
          "могут быть использованы в других отгрузках. Сама отгрузка и её " +
          "история сканирования удаляются безвозвратно.",
      )
    )
      return;
    setBusy(true);
    setErr(null);
    try {
      await api.deleteShipment(shipmentId);
      navigate("/shipments", { replace: true });
    } catch (e) {
      setErr(e instanceof HttpError ? `${e.status}: ${e.message}` : "сбой");
      setBusy(false);
    }
  }

  async function onClose() {
    if (
      !confirm(
        "Отгрузка физически передана в Ozon/WB/B24? Это финальный шаг — " +
          "все коды зафиксируются как использованные и больше не попадут ни в одну отгрузку.",
      )
    )
      return;
    setBusy(true);
    setErr(null);
    try {
      await api.closeShipment(shipmentId);
      await refresh();
    } catch (e) {
      setErr(e instanceof HttpError ? e.message : "сбой");
    } finally {
      setBusy(false);
    }
  }

  async function onRollback() {
    if (!shipment) return;
    if (
      !confirm(
        `Откатить статус «${SHIPMENT_STATUS_LABELS[shipment.status]}» на один шаг назад?\n\n` +
          "В чате Bitrix появится уведомление об откате — все участники увидят, что шаг переигран.",
      )
    )
      return;
    setBusy(true);
    setErr(null);
    try {
      await api.rollbackShipment(shipmentId);
      await refresh();
    } catch (e) {
      setErr(e instanceof HttpError ? `${e.status}: ${e.message}` : "сбой");
    } finally {
      setBusy(false);
    }
  }

  if (!shipment && !err) return <div className="container">Загрузка…</div>;

  // Сканирование разрешено только до «сборка завершена» — и только ролям,
  // у которых есть на это право на бэке: admin и scan_operator. Остальным
  // форму сканера не показываем, иначе они будут получать 403 на add_code.
  const canScanRole = user?.role === "admin" || user?.role === "scan_operator";
  // Перед стартом — оператор обязан выбрать режим (наборы / одиночные).
  // Пока не выбрал — формy сканера не показываем, чтобы не появилось
  // отсканированных кодов с неопределённым kit_index.
  const needsKitModeAnswer =
    canScanRole &&
    shipment !== null &&
    shipment.is_kit_order === null &&
    ["draft", "scanning"].includes(shipment.status);
  const canScan =
    canScanRole &&
    shipment &&
    ["draft", "scanning"].includes(shipment.status) &&
    !needsKitModeAnswer;
  // Активная партия — последняя по batch_no. В мульти-партийном режиме
  // прогресс считается ПО НЕЙ, а не по всей отгрузке. Зеркало
  // shipment.kit_size используется как fallback для legacy/одно-партийной
  // отгрузки, где kit_batches могут быть пустыми (до миграции).
  const sortedBatches = [...(shipment?.kit_batches ?? [])].sort(
    (a, b) => a.batch_no - b.batch_no,
  );
  const activeBatch =
    sortedBatches.length > 0 ? sortedBatches[sortedBatches.length - 1] : null;
  // В фазе обучения активной партии: kit_size = null.
  const isKitTraining =
    !!shipment &&
    shipment.is_kit_order === true &&
    (activeBatch ? activeBatch.kit_size === null : shipment.kit_size === null);
  // Прогресс активной партии.
  const codesCount = codes?.length ?? 0;
  const kitSize = activeBatch?.kit_size ?? shipment?.kit_size ?? null;
  const itemsInActiveBatch = activeBatch?.items_count ?? codesCount;
  const completedKitsInActive =
    shipment?.is_kit_order && kitSize
      ? Math.floor(itemsInActiveBatch / kitSize)
      : 0;
  const currentKitFilled =
    shipment?.is_kit_order && kitSize ? itemsInActiveBatch % kitSize : 0;
  // Сумма завершённых наборов по всем партиям (для общей сводки).
  const completedKits =
    shipment?.is_kit_order && sortedBatches.length > 0
      ? sortedBatches.reduce((acc, b) => acc + (b.kits_count ?? 0), 0)
      : shipment?.is_kit_order && shipment.kit_size
        ? Math.floor(codesCount / shipment.kit_size)
        : 0;
  // Кнопка «Сменить размер набора»: активная партия залочена и закрыта нацело.
  const canStartNewKitBatch =
    !!shipment &&
    shipment.is_kit_order === true &&
    shipment.status === "scanning" &&
    !!activeBatch &&
    activeBatch.kit_size !== null &&
    activeBatch.locked_at !== null &&
    itemsInActiveBatch > 0 &&
    itemsInActiveBatch % (activeBatch.kit_size ?? 1) === 0 &&
    canScanRole;
  // Кнопка «Завершить сборку» — из scanning, та же ролевая модель.
  // Дополнительно для kit-отгрузок блокируем, если есть незакрытый набор —
  // бэкенд всё равно отобьёт 409, но лучше показать заранее, что нельзя.
  const canPick =
    canScanRole &&
    shipment?.status === "scanning" &&
    (!shipment?.is_kit_order || !isKitTraining);
  // Кнопка «Скачать CSV» — только с booked (номер поставки уже есть) и позже.
  const canExport =
    shipment && ["booked", "exported", "in_turnover"].includes(shipment.status);
  // «Ввести в оборот» — только из exported (CSV уже скачан, TechShare подтвердил оборот).
  const canMarkInTurnover = shipment?.status === "exported";
  // Финальная кнопка «Отгружена» — только из in_turnover.
  const canClose = shipment?.status === "in_turnover";
  // «Удалить отгрузку»: admin — до closed; «Сканировка» — только черновик/сканируется
  // (пустые и ошибочные отгрузки, запрос склада 29.09.2026). Бэкенд проверяет то же.
  const canDelete =
    !!shipment &&
    ((user?.role === "admin" && shipment.status !== "closed") ||
      (user?.role === "scan_operator" &&
        (shipment.status === "draft" || shipment.status === "scanning")));
  // «Откатить на шаг назад» — склад (controller) и admin. С draft откатывать некуда.
  const canRollback =
    shipment &&
    (user?.role === "admin" || user?.role === "controller") &&
    shipment.status !== "draft" &&
    shipment.status !== "cancelled";

  return (
    <div className="container">
      {errorFlash && (
        <div
          aria-hidden
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(217, 30, 30, 0.65)",
            zIndex: 9998,
            pointerEvents: "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            animation: "boxFlash 3s ease-out forwards",
          }}
        >
          <div
            style={{
              color: "#fff",
              fontSize: 64,
              fontWeight: 900,
              letterSpacing: 4,
              textShadow: "0 0 12px #000",
            }}
          >
            СБОЙ
          </div>
        </div>
      )}

      {/* Sticky-bar: счётчик кодов всегда виден, даже когда столбец коробов
          уехал и таблица «Коды в отгрузке» оказалась глубоко внизу. */}
      {shipment && (
        <div
          style={{
            position: "sticky",
            top: 0,
            zIndex: 50,
            background: "#fff",
            padding: "8px 12px",
            borderBottom: "2px solid #ccc",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 12,
            marginBottom: 8,
          }}
        >
          <span style={{ fontWeight: 600 }}>
            <Link to="/shipments">← все</Link>
            {"  "}Отгрузка <span className="mono">{shipment.number}</span>{" "}
            <span className={`status status-${shipment.status}`}>
              {SHIPMENT_STATUS_LABELS[shipment.status]}
            </span>
            {shipment.ozon_client_id != null && (
              <span
                title="Юрлицо / кабинет Ozon отгрузки — против него идёт сличение и файл поставки"
                style={{
                  marginLeft: 10,
                  background: "#f6ffed",
                  border: "1px solid #b7eb8f",
                  color: "#389e0d",
                  borderRadius: 4,
                  padding: "2px 8px",
                  fontSize: 13,
                  fontWeight: 700,
                }}
              >
                🏢{" "}
                {cabinets.find((c) => c.ozon_client_id === shipment.ozon_client_id)
                  ?.name ?? `#${shipment.ozon_client_id}`}
              </span>
            )}
            {shipment.is_kit_order && kitSize !== null && (
              <span
                title={`Размер набора зафиксирован: ${kitSize} КИЗ`}
                style={{
                  marginLeft: 10,
                  background: "#fff7e6",
                  border: "1px solid #ffd591",
                  color: "#ad6800",
                  borderRadius: 4,
                  padding: "2px 8px",
                  fontSize: 13,
                  fontWeight: 700,
                }}
              >
                📦 Наборы по {kitSize}
              </span>
            )}
            {shipment.is_kit_order && kitSize === null && (
              <span
                title="Идёт обучение по первому набору"
                style={{
                  marginLeft: 10,
                  background: "#e6f7ff",
                  border: "1px solid #91d5ff",
                  color: "#096dd9",
                  borderRadius: 4,
                  padding: "2px 8px",
                  fontSize: 13,
                  fontWeight: 700,
                }}
              >
                🎓 Обучение
              </span>
            )}
          </span>
          <span style={{ fontSize: 22, fontWeight: 800 }}>
            {shipment.is_kit_order && kitSize !== null ? (
              <>
                Наборов:{" "}
                <span style={{ color: "#0a7", fontFamily: "monospace" }}>
                  {completedKits}
                </span>
                {currentKitFilled > 0 && (
                  <span
                    style={{ fontSize: 14, color: "#888", marginLeft: 8 }}
                    title={`В текущем наборе ${currentKitFilled} из ${kitSize}`}
                  >
                    +{currentKitFilled}/{kitSize}
                  </span>
                )}
                <span style={{ fontSize: 13, color: "#888", marginLeft: 10 }}>
                  ({codesCount} КИЗ)
                </span>
              </>
            ) : (
              <>
                Кодов:{" "}
                <span style={{ color: "#0a7", fontFamily: "monospace" }}>
                  {codes?.length ?? "…"}
                </span>
              </>
            )}
          </span>
        </div>
      )}

      {shipment && (
        <div className="card">
          <div className="row">
            <div>
              <div className="muted">id</div>
              <div>{shipment.id}</div>
            </div>
            <div>
              <div className="muted">название</div>
              <div>{shipment.name || "—"}</div>
            </div>
            <div>
              <div className="muted">кластер</div>
              <div className="mono">
                {shipment.cluster != null ? shipment.cluster : "—"}
              </div>
            </div>
            <div>
              <div className="muted">коробы / паллеты</div>
              <div className="mono">
                {shipment.boxes_actual != null
                  ? `${shipment.boxes_actual} / ${shipment.pallets_actual ?? Math.ceil(shipment.boxes_actual / 16)}`
                  : shipment.boxes_planned != null && shipment.boxes_planned > 0
                    ? `план: ${shipment.boxes_planned}`
                    : "—"}
              </div>
            </div>
            <div>
              <div className="muted">склад</div>
              <div>{shipment.warehouse_id || "—"}</div>
            </div>
            <div>
              <div className="muted">создана</div>
              <div>{formatMSK(shipment.created_at)}</div>
            </div>
            <div>
              <div className="muted">закрыта</div>
              <div>{formatMSK(shipment.closed_at)}</div>
            </div>
            <div className="grow" />
            {canPick && (
              <button
                className="primary"
                onClick={onPick}
                disabled={busy || !codes?.length}
                title="Завершить сканирование и отправить менеджеру маркетплейса"
              >
                Завершить сборку
              </button>
            )}
            <button
              onClick={onPackingList}
              disabled={busy || !codes?.length}
              title="Скачать упаковочный лист в формате Ozon «Состав ГМ поставки» (xlsx): раскладка по коробам и ШК ГМ из загруженных этикеток Ozon. Доступно для любой отгрузки, включая закрытые"
            >
              📦 Упаковочный лист
            </button>
            <button
              onClick={onExport}
              disabled={busy || !canExport}
              title={
                canExport
                  ? "Скачать CSV для загрузки в TechShare"
                  : "CSV станет доступен после того, как менеджер маркетплейса введёт номер поставки"
              }
            >
              Скачать CSV
            </button>
            {canMarkInTurnover && (
              <button
                className="primary"
                onClick={onMarkInTurnover}
                disabled={busy}
                title="TechShare принял оборот — отгрузка готова к физической передаче в Ozon/WB/B24"
              >
                Ввести в оборот
              </button>
            )}
            {canClose && (
              <button
                className="danger"
                onClick={onClose}
                disabled={busy}
                title="Физическая отгрузка выполнена: коды фиксируются как использованные"
              >
                Отгружена
              </button>
            )}
            {canRollback && (
              <button
                onClick={onRollback}
                disabled={busy}
                title="Вернуть статус на один шаг назад (склад/админ). Уведомляет чат."
              >
                ⏪ Откатить
              </button>
            )}
            {canDelete && (
              <button
                className="danger"
                onClick={onDelete}
                disabled={busy}
                title="Удалить отгрузку. Коды вернутся в «Свободно»."
              >
                Удалить
              </button>
            )}
          </div>
          {shipment.comment && <div className="muted" style={{ marginTop: 8 }}>{shipment.comment}</div>}
        </div>
      )}

      {shipment && <OzonSupplyPanel shipment={shipment} />}

      {shipment && <DeliveryPanel shipment={shipment} onSaved={refresh} />}

      {canScan && (
        <div className="card">
          <h2>Добавить код (сканирование)</h2>

          <div
            style={{
              display: "flex",
              gap: 16,
              alignItems: "flex-start",
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="row" style={{ marginBottom: 12 }}>
                <ScannerIndicator status={scannerStatus} />
                {scannerStatus.kind === "connected" ? (
                  <button onClick={onDisconnectScanner}>Отключить сканер</button>
                ) : scannerStatus.kind === "unsupported" ? (
                  <button disabled title="браузер не поддерживает Web Serial API (нужен Chrome/Edge по HTTPS)">
                    Web Serial не поддерживается
                  </button>
                ) : (
                  <button
                    className="primary"
                    onClick={onConnectScanner}
                    disabled={scannerStatus.kind === "connecting"}
                  >
                    {scannerStatus.kind === "connecting" ? "Подключение…" : "Подключить сканер"}
                  </button>
                )}
                <span className="muted" style={{ fontSize: 12 }}>
                  {scannerStatus.kind === "connected"
                    ? "сканируй коды — они автоматически добавятся в отгрузку"
                    : "или введи composite_key вручную →"}
                </span>
              </div>

              <form onSubmit={onScan}>
                <div className="row">
                  <input
                    ref={inputRef}
                    type="text"
                    placeholder="composite_key (GTIN+serial)"
                    value={scanInput}
                    onChange={(e) => setScanInput(e.target.value)}
                    className="grow mono"
                    autoFocus
                  />
                  <button className="primary" disabled={busy || !scanInput.trim()}>
                    Добавить
                  </button>
                </div>
              </form>
              {err && <div className="error">{err}</div>}
              {lastScanOk && <div className="ok">{lastScanOk}</div>}

              {isKitTraining && (
                <div
                  style={{
                    marginTop: 12,
                    padding: 12,
                    background: "#e6f7ff",
                    border: "1px solid #91d5ff",
                    borderRadius: 6,
                  }}
                >
                  <div
                    style={{
                      fontSize: 14,
                      fontWeight: 600,
                      color: "#0050b3",
                      marginBottom: 6,
                    }}
                  >
                    🎓 Обучение по {activeBatch && activeBatch.batch_no > 1
                      ? `новой партии №${activeBatch.batch_no}`
                      : "первому набору"}
                  </div>
                  <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>
                    Отсканируйте полностью {activeBatch && activeBatch.batch_no > 1
                      ? "первый набор новой партии"
                      : "первый набор"}{" "}
                    и нажмите «Набор собран». Система запомнит размер и
                    применит его к следующим наборам этой партии.
                  </div>
                  <div
                    style={{
                      display: "flex",
                      gap: 8,
                      alignItems: "center",
                      flexWrap: "wrap",
                    }}
                  >
                    <span
                      style={{
                        fontSize: 18,
                        fontWeight: 700,
                        color: "#0050b3",
                      }}
                    >
                      В наборе: {itemsInActiveBatch}
                    </span>
                    <button
                      className="primary"
                      onClick={onMarkKitDone}
                      disabled={kitBusy || itemsInActiveBatch === 0}
                      title="Зафиксировать размер набора"
                      style={{ fontSize: 15, padding: "8px 16px" }}
                    >
                      ✓ Набор собран
                    </button>
                    <button
                      onClick={() => {
                        void onResetKitTraining();
                      }}
                      disabled={kitBusy || itemsInActiveBatch === 0}
                      title="Удалить отсканированные коды и начать обучение заново"
                    >
                      ↻ Сбросить и пересдать
                    </button>
                  </div>
                </div>
              )}

              {shipment?.is_kit_order && kitSize !== null && (
                <div
                  style={{
                    marginTop: 12,
                    padding: "8px 12px",
                    background: "#fff7e6",
                    border: "1px solid #ffd591",
                    borderRadius: 6,
                    fontSize: 13,
                  }}
                >
                  📦 <b>Режим наборов</b>
                  {sortedBatches.length > 1 && (
                    <>: партий <b>{sortedBatches.length}</b>, общая сводка:{" "}
                    {sortedBatches
                      .filter((b) => b.kit_size)
                      .map(
                        (b) =>
                          `№${b.batch_no} × ${b.kit_size} шт = ${b.kits_count ?? 0} набор(ов)`,
                      )
                      .join("; ")}
                    .{" "}</>
                  )}
                  {sortedBatches.length <= 1 && (
                    <>: kit_size = {kitSize} КИЗ. </>
                  )}
                  Активная партия №{activeBatch?.batch_no ?? 1}: завершено{" "}
                  <b>{completedKitsInActive}</b>
                  {currentKitFilled > 0 && (
                    <>
                      , в текущем: <b>{currentKitFilled}</b>/{kitSize}
                    </>
                  )}
                  . Всего наборов в отгрузке: <b>{completedKits}</b>.
                  {canStartNewKitBatch && (
                    <div style={{ marginTop: 8 }}>
                      <button
                        onClick={() => {
                          void onStartNewKitBatch();
                        }}
                        disabled={kitBusy}
                        title="Закончить текущую партию и начать новую с другим размером"
                        style={{
                          background: "#fff",
                          border: "1px solid #ad6800",
                          color: "#ad6800",
                          padding: "6px 12px",
                          borderRadius: 4,
                          fontWeight: 600,
                        }}
                      >
                        🔄 Сменить размер набора
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {shipment?.boxes_planned && shipment.boxes_planned > 0 ? (
              <BoxesPanel
                planned={shipment.boxes_planned}
                printed={boxes}
                busyNumber={boxBusy}
                onPrint={onPrintBox}
                onAddBox={onAddBoxSlot}
                addBusy={addBoxBusy}
                active={shipment.active_box_number ?? null}
              />
            ) : (
              // У отгрузок, созданных до фичи стикеров, boxes_planned = NULL и
              // самой панели нет. Показываем одну кнопку — её клик создаст
              // первый короб; дальше уже работает обычный BoxesPanel с «+ короб».
              <div
                style={{
                  flex: "0 0 110px",
                  borderLeft: "1px solid var(--border, #e0e0e0)",
                  paddingLeft: 12,
                }}
              >
                <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>
                  Стикеры коробов 58×40
                </div>
                <button
                  type="button"
                  onClick={onAddBoxSlot}
                  disabled={addBoxBusy}
                  title="включить учёт коробов: создать первый короб"
                  style={{
                    width: "100%",
                    padding: "8px 0",
                    fontSize: 12,
                    cursor: addBoxBusy ? "wait" : "pointer",
                  }}
                >
                  {addBoxBusy ? "…" : "+ короб"}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {pickDialog && shipment && (
        <PickFinalizeModal
          shipment={shipment}
          initialBoxes={pickDialog.boxesActual}
          printedCount={boxes.filter((b) => b.printed_at !== null).length}
          busy={busy}
          onCancel={() => setPickDialog(null)}
          onAccept={(actual) => {
            void doPick(actual);
          }}
        />
      )}

      {needsKitModeAnswer && shipment && (
        <KitModePromptModal
          shipmentNumber={shipment.number}
          busy={kitBusy}
          onAnswer={(isKit) => {
            void onAnswerKitMode(isKit);
          }}
        />
      )}

      {confirmKitOpen && (
        <KitConfirmSizeModal
          size={codesCount}
          busy={kitBusy}
          onConfirm={() => {
            void onConfirmKitSize();
          }}
          onReset={() => {
            void onResetKitTraining();
          }}
        />
      )}

      <div className="card">
        <h2>Коды в отгрузке {codes && <span className="muted">({codes.length})</span>}</h2>
        {codes === null ? (
          <div>Загрузка…</div>
        ) : codes.length === 0 ? (
          <div className="muted">Ещё ни одного кода не добавлено.</div>
        ) : (
          <div
            ref={codesScrollRef}
            style={{ maxHeight: "70vh", overflowY: "auto", overflowX: "auto" }}
          >
            <table>
              <thead
                style={{
                  position: "sticky",
                  top: 0,
                  zIndex: 1,
                  background: "var(--card-bg)",
                }}
              >
                <tr>
                  <th>№</th>
                  {shipment?.is_kit_order && kitSize !== null && <th>Набор</th>}
                  <th>id</th>
                  <th>GTIN</th>
                  <th>Serial</th>
                  <th>Статус</th>
                  <th>Composite key</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  // Виртуализация через spacer-строки: сохраняет нативный
                  // table-layout (колонки выравнены по thead, печать/выделение
                  // видимого целы), в отличие от position:absolute.
                  const vItems = rowVirtualizer.getVirtualItems();
                  const colCount =
                    shipment?.is_kit_order && kitSize !== null ? 8 : 7;
                  const padTop = vItems.length ? vItems[0].start : 0;
                  const padBottom = vItems.length
                    ? rowVirtualizer.getTotalSize() - vItems[vItems.length - 1].end
                    : 0;
                  const spacer = (h: number, key: string) =>
                    h > 0 ? (
                      <tr key={key} style={{ height: h }}>
                        <td colSpan={colCount} style={{ padding: 0, border: "none" }} />
                      </tr>
                    ) : null;
                  return (
                    <>
                      {spacer(padTop, "pad-top")}
                      {vItems.map((vi) => {
                        const c = codes[vi.index];
                        const i = vi.index;
                        // kit_index считаем на фронте: коды отсортированы по
                        // добавлению, kit_size зафиксирован, формула = бэкенд.
                        const kitIdx =
                          shipment?.is_kit_order && kitSize !== null
                            ? Math.floor(i / kitSize) + 1
                            : null;
                        return (
                          <tr
                            key={c.id}
                            data-index={i}
                            ref={rowVirtualizer.measureElement}
                          >
                            <td className="mono">{i + 1}</td>
                            {shipment?.is_kit_order && kitSize !== null && (
                              <td
                                className="mono"
                                style={{ color: "#ad6800", fontWeight: 600 }}
                              >
                                #{kitIdx}
                              </td>
                            )}
                            <td>{c.id}</td>
                            <td className="mono">{c.gtin}</td>
                            <td className="mono">{c.serial}</td>
                            <td>
                              <span className={`status status-${c.code_status}`}>
                                {c.code_status}
                              </span>
                            </td>
                            <td className="mono">{c.composite_key}</td>
                            <td className="right">
                              {canScan && (
                                <button
                                  onClick={() => onRemove(c.id)}
                                  title="убрать из отгрузки"
                                >
                                  ×
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                      {spacer(padBottom, "pad-bottom")}
                    </>
                  );
                })()}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
